import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import '../style/filter.less';
import { BackIcon, FilterMinusIcon, ResetIcon } from "../icons";

// Utility function to highlight matching text
const highlightMatch = (text: string, query: string): JSX.Element => {
  if (!query) return <span>{text}</span>;

  const parts = text.split(new RegExp(`(${query})`, 'gi'));
  return (
    <span>
      {parts.map((part, index) =>
        part.toLowerCase() === query.toLowerCase() ?
          <span key={index} className="highlight-match">{part}</span> :
          <span key={index}>{part}</span>
      )}
    </span>
  );
};

interface FilterComponentProps {
  close: (filterObj: FilterObject, keepOpen?: boolean) => void;
  isExpandClass: string;
  selectionFilter: FilterObject;
  multiSelectOptions?: Array<{
    tag: string;
    label: string;
    value: string;
    originalValue?: string; // Store original backend value with proper casing
  }>;
  tripGroupOptions?: Array<{
    tag: string;
    label: string;
    value: string;
    originalValue?: string; // Store original backend value with proper casing
  }>;
  passengerOptions?: Array<{
    tag: string;
    label: string;
    value: string;
    originalValue?: string; // Store original backend value with proper casing
  }>;
  recordLocatorOptions?: Array<{
    tag: string;
    label: string;
    value: string;
    originalValue?: string; // Store original backend value with proper casing
  }>;
  agentNameOptions?: Array<{
    tag: string;
    label: string;
    value: string;
    originalValue?: string; // Store original backend value with proper casing
  }>;
}

interface SelectionList {
  label: string;
  value: string;
}

export interface FilterObject {
  selectionType: string;
  referenceNumber: object[];
  agentName: object[];
  tripGroup: object[];
  passengerName: object[];
}

export type FilterComponentRef = {
  searchResults: () => FilterObject;
  getFilterCount: () => number;
};

// Confirmation Dialog Component
interface ConfirmDialogProps {
  isOpen: boolean;
  title: string;
  message: string;
  confirmText: string;
  cancelText: string;
  onConfirm: () => void;
  onCancel: () => void;
}

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  isOpen, title, message, confirmText, cancelText, onConfirm, onCancel
}) => {
  if (!isOpen) return null;

  return (
    <div className="confirm-dialog-overlay">
      <div className="confirm-dialog issue-resolution-modal modal-dialog modal-md">
        <div className="title">
          {title}
          <a onClick={onCancel}>
            <i className="cancel pull-right glyphicon icon-white glyphicon-remove"></i>
          </a>
        </div>
        <div className="content">
          <div className="choices">
            <div className="row">
              <div className="col-md-12 center">{message}</div>
            </div>
          </div>
          <div className="action">
            <button
              className="btn btn-default modal-btn-margin-right"
              onClick={onCancel}
            >
              {cancelText}
            </button>
            <button
              className="btn btn-default button-action"
              onClick={onConfirm}
            >
              {confirmText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const selectionLists: SelectionList[] = [
  {
    label: 'All',
    value: 'All'
  },
  {
    label: 'Trips with issues',
    value: 'Trips with issues'
  },
  {
    label: 'My issues',
    value: 'My issues'
  }
];

// Custom MultiSelect component
interface MultiSelectProps {
  label: string;
  options: Array<{
    tag: string;
    label: string;
    value: string;
    originalValue?: string; // Store original backend value with proper casing
  }>;
  selectedOptions: Array<any>;
  onChange: (selected: Array<any>) => void;
  resetKey?: number;
  disabled?: boolean;
}

const MultiSelect: React.FC<MultiSelectProps> = ({ label, options, selectedOptions, onChange, resetKey, disabled = false }) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [filteredOptions, setFilteredOptions] = useState<Array<any>>([]);
  const [searchValue, setSearchValue] = useState<string>('');
  const [isScrollable, setIsScrollable] = useState<boolean>(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const selectedRef = useRef<HTMLDivElement>(null);

  // Clear input and close dropdown when resetKey changes
  useEffect(() => {
    setSearchValue('');
    setIsOpen(false);
  }, [resetKey]);

  // Check if content is scrollable
  useEffect(() => {
    if (selectedRef.current) {
      const { scrollHeight, clientHeight } = selectedRef.current;
      setIsScrollable(scrollHeight > clientHeight);
    }
  }, [selectedOptions]);

  // Determine size class based on number of selected options
  const getSizeClass = () => {
    if (selectedOptions.length === 0) return 'empty';
    if (selectedOptions.length <= 2) return 'small';
    if (selectedOptions.length <= 4) return 'medium';
    return 'large';
  };

  // Filter available options to exclude already selected ones
  useEffect(() => {
    const selectedValues = selectedOptions.map(option => option.value);
    const availableOptions = options.filter(option => !selectedValues.includes(option.value));

    // If there's a search value, filter by it; otherwise show all available options
    if (searchValue) {
      const filtered = availableOptions.filter(
        option => (option.originalValue || option.label).toLowerCase().includes(searchValue.toLowerCase())
      );
      setFilteredOptions(filtered);
    } else {
      setFilteredOptions(availableOptions);
    }
  }, [options, selectedOptions, searchValue]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSelect = (option: any) => {
    if (disabled) return;
    onChange([...selectedOptions, option]);
    setSearchValue('');
    setIsOpen(false);
  };

  const handleRemove = (option: any) => {
    if (disabled) return;
    onChange(selectedOptions.filter(item => item.value !== option.value));
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchValue(value);

    // Always filter from the original available options (excluding selected ones)
    const selectedValues = selectedOptions.map(option => option.value);
    const availableOptions = options.filter(option => !selectedValues.includes(option.value));

    if (value) {
      const filtered = availableOptions.filter(
        option => (option.originalValue || option.label).toLowerCase().includes(value.toLowerCase())
      );
      setFilteredOptions(filtered);
    } else {
      // Reset to show all available options when search is cleared
      setFilteredOptions(availableOptions);
    }
  };

  return (
    <div className={`custom-multiselect ${disabled ? 'disabled' : ''}`} ref={dropdownRef}>
      <div className="multiselect-label">{label}</div>
      <div className={`multiselect-container ${isScrollable ? 'scrollable' : ''} ${disabled ? 'disabled' : ''}`}>
        {/* Selected Options Pills */}
        <div
          className={`multiselect-selected ${getSizeClass()} ${disabled ? 'disabled' : ''}`}
          ref={selectedRef}
          onClick={() => !disabled && setIsOpen(true)}
        >
          {selectedOptions.map(option => (
            <div
              key={option.value}
              className="multiselect-pill fs-exclude"
              title={option.originalValue || option.label}
              data-fs-exclude
            >
              <span className="pill-text">{option.originalValue || option.label}</span>
              <button
                className="pill-remove"
                onClick={(e) => {
                  e.stopPropagation(); // Prevent opening dropdown when removing pill
                  handleRemove(option);
                }}
                disabled={disabled}
              >
                ×
              </button>
            </div>
          ))}
          <input
            type="text"
            className="multiselect-input fs-exclude"
            placeholder={selectedOptions.length === 0 ? `Select ${label}...` : ""}
            onClick={() => !disabled && setIsOpen(true)}
            onFocus={() => !disabled && setIsOpen(true)}
            onChange={handleSearch}
            value={searchValue}
            disabled={disabled}
            data-fs-exclude
          />
        </div>

        {/* Dropdown */}
        {isOpen && filteredOptions.length > 0 && !disabled && (
          <div className="multiselect-dropdown scrollContent fs-exclude" data-fs-exclude>
            {filteredOptions.map(option => (
              <div
                key={option.value}
                className="option"
                onClick={() => handleSelect(option)}
              >
                {searchValue ? highlightMatch(option.originalValue || option.label, searchValue) : (option.originalValue || option.label)}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Custom Radio Group Component
interface RadioGroupProps {
  options: SelectionList[];
  value: string;
  onChange: (value: string) => void;
}

const RadioGroup: React.FC<RadioGroupProps> = ({ options, value, onChange }) => {
  return (
    <div className="custom-radio-group">
      {options.map((option) => (
        <label key={option.value} className="custom-radio">
          <input
            type="radio"
            value={option.value}
            checked={value === option.value}
            onChange={() => onChange(option.value)}
          />
          <span className="radio-label">{option.label}</span>
        </label>
      ))}
    </div>
  );
};

// Custom Text Input Component
interface TextInputProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  suggestions?: Array<{ tag: string, label: string, value: string, originalValue?: string }>;
}

const TextInput: React.FC<TextInputProps> = ({ label, value, onChange, suggestions }) => {
  const [showSuggestions, setShowSuggestions] = useState<boolean>(false);
  const [filteredSuggestions, setFilteredSuggestions] = useState<Array<{ tag: string, label: string, value: string, originalValue?: string }>>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  // Filter suggestions based on input
  useEffect(() => {
    if (suggestions && value) {
      const filtered = suggestions.filter(
        suggestion => (suggestion.originalValue || suggestion.value).toLowerCase().includes(value.toLowerCase())
      ).slice(0, 10); // Limit to 10 suggestions for performance
      setFilteredSuggestions(filtered);
    } else {
      setFilteredSuggestions([]);
    }
  }, [value, suggestions]);

  // Handle click outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (inputRef.current && !inputRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className="custom-text-input" ref={inputRef}>
      <label className="input-label">{label}</label>
      <input
        type="text"
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          setShowSuggestions(true);
        }}
        onFocus={() => setShowSuggestions(true)}
        className="text-input"
        placeholder={`Enter ${label}...`}
      />

      {/* Suggestions dropdown */}
      {showSuggestions && filteredSuggestions.length > 0 && (
        <div className="text-suggestions">
          {filteredSuggestions.map((suggestion, index) => (
            <div
              key={index}
              className="suggestion-item"
              onClick={() => {
                onChange(suggestion.originalValue || suggestion.value);
                setShowSuggestions(false);
              }}
            >
              {value ? highlightMatch(suggestion.originalValue || suggestion.label, value) : (suggestion.originalValue || suggestion.label)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const FilterComponent = forwardRef<FilterComponentRef, FilterComponentProps>(
  ({ close, isExpandClass, selectionFilter, multiSelectOptions = [], tripGroupOptions = [], passengerOptions = [], recordLocatorOptions = [], agentNameOptions = [] }, ref) => {
    const [statusSection, setStatusSection] = useState<boolean>(true);
    const [passengerSection, setPassengerSection] = useState<boolean>(true);
    const [assignedSection, setAssignedSection] = useState<boolean>(true);

    // Define state setters that update parent component immediately
    const [selectionType, setSelectionTypeState] = useState<string>(selectionFilter.selectionType);
    const [referenceNumber, setReferenceNumberState] = useState<object[]>(selectionFilter.referenceNumber);
    const [agentName, setAgentNameState] = useState<object[]>(selectionFilter.agentName);
    const [tripGroup, setTripGroupState] = useState<object[]>(selectionFilter.tripGroup);
    const [passengerName, setPassengerNameState] = useState<object[]>(selectionFilter.passengerName);
    // Add resetKey to force reset of child inputs
    const [resetKey, setResetKey] = useState<number>(0);

    // Confirmation dialog state
    const [showConfirmDialog, setShowConfirmDialog] = useState<boolean>(false);
    const [pendingSelectionType, setPendingSelectionType] = useState<string>('');

    // Define wrapper functions to update state and notify parent
    const setSelectionType = (value: string) => {
      // Check if switching to "My issues" with existing agent selections
      if (value === 'My issues' && agentName.length > 0) {
        setPendingSelectionType(value);
        setShowConfirmDialog(true);
        return; // Don't proceed until user confirms
      }

      // Normal flow for other selections
      setSelectionTypeState(value);
      const filterObj = {
        selectionType: value,
        referenceNumber,
        agentName,
        tripGroup,
        passengerName
      };
      close(filterObj, true); // Keep panel open
    };

    const setReferenceNumber = (value: object[]) => {
      setReferenceNumberState(value);
      const filterObj = {
        selectionType,
        referenceNumber: value,
        agentName,
        tripGroup,
        passengerName
      };
      close(filterObj, true); // Keep panel open
    };

    const setAgentName = (value: object[]) => {
      setAgentNameState(value);
      const filterObj = {
        selectionType,
        referenceNumber,
        agentName: value,
        tripGroup,
        passengerName
      };
      close(filterObj, true); // Keep panel open
    };

    const setTripGroup = (value: object[]) => {
      setTripGroupState(value);
      const filterObj = {
        selectionType,
        referenceNumber,
        agentName,
        tripGroup: value,
        passengerName
      };
      close(filterObj, true); // Keep panel open
    };

    const setPassengerName = (value: object[]) => {
      setPassengerNameState(value);
      const filterObj = {
        selectionType,
        referenceNumber,
        agentName,
        tripGroup,
        passengerName: value
      };
      close(filterObj, true); // Keep panel open
    };

    // Confirmation dialog handlers
    const handleConfirmClearAgents = () => {
      // User confirmed - clear agents and proceed
      setAgentNameState([]);
      setSelectionTypeState(pendingSelectionType);
      setResetKey(prev => prev + 1); // Reset the MultiSelect component

      const filterObj = {
        selectionType: pendingSelectionType,
        referenceNumber,
        agentName: [], // Cleared
        tripGroup,
        passengerName
      };

      setShowConfirmDialog(false);
      setPendingSelectionType('');
      close(filterObj, true);
    };

    const handleCancelClearAgents = () => {
      // User cancelled - keep current state
      setShowConfirmDialog(false);
      setPendingSelectionType('');
    };

    // Handle reset function to clear all filters without closing the panel
    const handleReset = () => {
      // Reset all filter values to default by updating the parent directly
      // This will trigger a re-render with updated values
      const resetObj = {
        selectionType: 'All',
        referenceNumber: [],
        agentName: [],
        tripGroup: [],
        passengerName: []
      };
      // Update local state
      setSelectionTypeState('All');
      setReferenceNumberState([]);
      setAgentNameState([]);
      setTripGroupState([]);
      setPassengerNameState([]);
      // Increment resetKey to force child input reset
      setResetKey(prev => prev + 1);
      // Call close with keepOpen=true to prevent panel from closing
      close(resetObj, true);
    };

    // Use separate options or fall back to multiSelectOptions for backward compatibility
    const effectiveTripGroupOptions = tripGroupOptions.length > 0 ? tripGroupOptions : multiSelectOptions;
    const effectivePassengerOptions = passengerOptions.length > 0 ? passengerOptions : [];

    // Handle click outside to close the filter panel only (no need to reapply filters)
    const filterRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (filterRef.current && !filterRef.current.contains(event.target as Node)) {
          // Just close the panel without sending filter updates again
          // Pass the current selectionFilter instead of recalculating to prevent filter reapplication
          close(selectionFilter, false);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [selectionFilter]);

    const headerTemplate = (label: string, setState: React.Dispatch<React.SetStateAction<boolean>>, state: boolean) => {
      return (
        <div className='sectionHeader'>
          <div className="sectionText">{label}</div>
          <div className="sectionIcon" onClick={() => setState(!state)}>
            <FilterMinusIcon />
          </div>
        </div>
      );
    };

    const searchResults = () => {
      // Return the current filter state
      let obj = {
        selectionType,
        referenceNumber,
        agentName,
        tripGroup,
        passengerName
      };
      return obj;
    };

    const getFilterCount = () => {
      const filterObj = searchResults();
      // Start with 1 by default (because "ALL" is selected)
      let count = 1;

      // Count each selected value in arrays
      if (filterObj.tripGroup && Array.isArray(filterObj.tripGroup) && filterObj.tripGroup.length > 0) {
        count += filterObj.tripGroup.length;
      }

      if (filterObj.passengerName && Array.isArray(filterObj.passengerName) && filterObj.passengerName.length > 0) {
        count += filterObj.passengerName.length;
      }

      if (filterObj.referenceNumber && Array.isArray(filterObj.referenceNumber) && filterObj.referenceNumber.length > 0) {
        count += filterObj.referenceNumber.length;
      }

      if (filterObj.agentName && Array.isArray(filterObj.agentName) && filterObj.agentName.length > 0) {
        count += filterObj.agentName.length;
      }

      // If selection type is not "All", add 1 more to the count
      if (filterObj.selectionType && filterObj.selectionType !== 'All') {
        count += 1;
      }

      return count;
    };

    useImperativeHandle(ref, () => ({
      searchResults,
      getFilterCount
    }));

    return (
      <div className={`filterLayer ${isExpandClass}`} ref={filterRef}>
        <div className='filterHeader evolve-header'>
          <div
            className="filterBack evolve-btn"
            onClick={() => {
              // Only close the panel without applying filters again
              close(selectionFilter, false); // Use current selectionFilter instead of recalculating
            }}
          >
            <BackIcon />
          </div>
          <div className="filterText">Filter</div>
          <div
            className="filterParent evolve-reset-btn"
            onClick={handleReset}
          >
            <div className="filterReset">
              <ResetIcon />
            </div>
            <div className="filterResetText">Reset</div>
          </div>
        </div>
        <div className='filterBody scrollContent'>
          <div className="statusSection filter-section">
            {headerTemplate('Status', setStatusSection, statusSection)}
            {statusSection && (
              <div className="sectionBody evolve-section-body">
                <div className="radioParent">
                  <RadioGroup
                    options={selectionLists}
                    value={selectionType}
                    onChange={setSelectionType}
                  />
                </div>
              </div>
            )}
          </div>
          <div className="passengerSection filter-section">
            {headerTemplate('Passenger', setPassengerSection, passengerSection)}
            {passengerSection && (
              <div className="sectionBody evolve-section-body">
                <div className="multiselect fs-exclude" data-fs-exclude>
                  <MultiSelect
                    label="Trip Group"
                    options={effectiveTripGroupOptions}
                    selectedOptions={tripGroup}
                    onChange={setTripGroup}
                    resetKey={resetKey}
                  />
                </div>
                <div className="multiselect evolve-form-group fs-exclude" data-fs-exclude>
                  <MultiSelect
                    label="Passenger Name"
                    options={effectivePassengerOptions}
                    selectedOptions={passengerName}
                    onChange={setPassengerName}
                    resetKey={resetKey}
                  />
                </div>
                <div className="textfield evolve-form-group fs-exclude" data-fs-exclude>
                  <MultiSelect
                    label="Reference Number"
                    options={recordLocatorOptions}
                    selectedOptions={referenceNumber}
                    onChange={setReferenceNumber}
                    resetKey={resetKey}
                  />
                </div>
              </div>
            )}
          </div>
          <div className="assignedSection filter-section">
            {headerTemplate('Assigned to', setAssignedSection, assignedSection)}
            {assignedSection && (
              <div className="sectionBody evolve-section-body fs-exclude" data-fs-exclude>
                <div className={`textfield ${selectionType === 'My issues' ? 'disabled' : ''}`}>
                  <MultiSelect
                    label="Agent Name"
                    options={agentNameOptions}
                    selectedOptions={agentName}
                    onChange={setAgentName}
                    resetKey={resetKey}
                    disabled={selectionType === 'My issues'}
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Confirmation Dialog */}
        <ConfirmDialog
          isOpen={showConfirmDialog}
          title="Clear Agent Filters?"
          message={`You have ${agentName.length} agent${agentName.length > 1 ? 's' : ''} selected. Switching to "My issues" will clear these selections. Continue?`}
          confirmText="Proceed"
          cancelText="Cancel"
          onConfirm={handleConfirmClearAgents}
          onCancel={handleCancelClearAgents}
        />
      </div>
    );
  }
);

export default FilterComponent;